import React from 'react';
import type { ViewProps } from 'react-native';
import { Pressable, StyleSheet, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useStyle } from 'react-native-style-utilities';
import {
  BodyText,
  Button,
  Container,
  DisplayText,
  HStack,
  Icon,
  theme,
  TitleText,
  useBreakpointHelpers,
  useResponsive,
  VStack,
} from '@sg/garnish';

import { useLocalizationContext } from '@order/Localization';

import type { SweetpassHomeStatsContent } from '../../hooks';
import type { SweetpassHeroStatsPalette } from './SweetpassHomeHeroStats.utils';

// ─────────────────────────────────────────────────────────────────────────────

export const SweetpassHomeHeroStats = (props: SweetpassHomeHeroStatsProps) => {
  const { match } = useResponsive();

  // ─── Styles ──────────────────────────────────────────────────────────

  const paletteBackgroundStyle = useStyle(
    () => ({ backgroundColor: props.palette.backgroundColor }),
    [props.palette.backgroundColor],
  );
  const outerContainerStyles = [
    paletteBackgroundStyle,
    styles.outerContainer,
    match([styles.outerContainerXS, styles.outerContainerSM]),
  ];
  const wrapperResponsiveStyles = match([
    styles.wrapperXS,
    styles.wrapperSM,
    styles.wrapperMD,
  ]);
  const wrapperStyles = [wrapperResponsiveStyles];

  // ─────────────────────────────────────────────────────────────────────

  const wrapperTestID = match([
    'sweetpass.home-hero-stats.mobile',
    'sweetpass.home-hero-stats.desktop',
  ]);

  return (
    <SafeAreaView edges={['top']} style={outerContainerStyles}>
      <Container testID={wrapperTestID} style={wrapperStyles}>
        <HeroStatsCard {...props} />
      </Container>
    </SafeAreaView>
  );
};

const HeroStatsCard = ({
  testID,
  palette,
  content,
  detailedHeroStats,
  navigateToBenefits,
  ...rest
}: SweetpassHomeHeroStatsProps) => {
  const { isMobile } = useBreakpointHelpers();

  const titleStyleNormal = useCustomTitleStyle(palette.textColor, false);
  const titleStyleHighlight = useCustomTitleStyle(palette.textColor, true);
  const textStyle = useStyle(
    () => ({ color: palette.textColor }),
    [palette.textColor],
  );

  return (
    <VStack divider dividerColor={palette.dividerColor} customSpacing={0}>
      <View style={styles.heroStatsColumn}>
        <View style={styles.heroStatsTitle}>
          <DisplayText style={titleStyleNormal} size={2}>
            {content.headerNameText}
          </DisplayText>
          <TitleText style={titleStyleHighlight} size={4}>
            {content.headerMembershipText}
          </TitleText>
        </View>

        <BodyText style={[styles.heroStatsNotice, textStyle]} size={2}>
          {content.infoText}
        </BodyText>
      </View>

      {detailedHeroStats ? (
        <HeroStatsDetailed testID={testID} palette={palette} {...rest} />
      ) : (
        <HeroStatsSimple testID={testID} palette={palette} {...rest} />
      )}

      <View style={styles.heroStatsColumn}>
        <BodyText style={[styles.heroStatsNotice, textStyle]} size={3}>
          {content.noticeText}
        </BodyText>

        <Button
          testID="sweetpass.home-hero-stats.cta"
          palette={palette.ctaPalette}
          style={styles.heroStatsCta}
          size={isMobile ? 'medium' : 'large'}
          onPress={navigateToBenefits}
        >
          {content.ctaText}
        </Button>
      </View>
    </VStack>
  );
};

const HeroStatsSimple = ({
  palette,
  rewardsCount,
  challengesCount,
  navigateToRewards,
  navigateToChallenges,
}: HeroStatsVariantProps) => {
  const { t } = useLocalizationContext();
  const { match } = useResponsive();

  const borderStyle = useStyle(
    () => ({ borderRightColor: palette.dividerColor }),
    [palette.dividerColor],
  );

  const bodyTextSize = match([4, 3, 2] as const);
  const bodyTextStyle = useStyle(
    () => ({ color: palette.textColor }),
    [palette.textColor],
  );

  const iconStyle = useStyle(() => ({
    flexShrink: 0,
    paddingLeft: match([
      theme.spacing.small,
      theme.spacing.small,
      theme.spacing.medium,
    ]),
  }));

  return (
    <VStack
      testID="sweetpass.home-hero-stats.simple"
      divider
      dividerColor={palette.dividerColor}
      customSpacing={0}
    >
      <HStack itemsPerRow={2} customSpacing={0}>
        <Pressable
          testID="sweetpass.home-hero-stats.rewards"
          accessibilityRole="button"
          style={[styles.heroStatsRow, styles.heroStatsLeft, borderStyle]}
          onPress={navigateToRewards}
        >
          <TitleText style={bodyTextStyle} size={2}>
            {rewardsCount}
          </TitleText>

          <View style={[styles.heroStatsRow, styles.heroStatsAlignBottom]}>
            <BodyText style={bodyTextStyle} size={bodyTextSize}>
              {rewardsCount === 1
                ? t('sweetpass.home.stats.reward')
                : t('sweetpass.home.stats.rewards')}
            </BodyText>

            <Icon
              name="IconArrowRight"
              style={iconStyle}
              color={palette.textColor}
              height={24}
              width={24}
            />
          </View>
        </Pressable>

        <Pressable
          testID="sweetpass.home-hero-stats.challenges"
          accessibilityRole="button"
          style={[styles.heroStatsRow, styles.heroStatsRight]}
          onPress={navigateToChallenges}
        >
          <TitleText style={bodyTextStyle} size={2}>
            {challengesCount}
          </TitleText>

          <View style={[styles.heroStatsRow, styles.heroStatsAlignBottom]}>
            <BodyText style={bodyTextStyle} size={bodyTextSize}>
              {challengesCount === 1
                ? t('sweetpass.home.stats.challenge')
                : t('sweetpass.home.stats.challenges')}
            </BodyText>

            <Icon
              name="IconArrowRight"
              style={iconStyle}
              color={palette.textColor}
              height={24}
              width={24}
            />
          </View>
        </Pressable>
      </HStack>
    </VStack>
  );
};

const HeroStatsDetailed = ({
  palette,
  amountSaved,
  rewardsCount,
  challengesCount,
  navigateToRewards,
  navigateToChallenges,
}: HeroStatsVariantProps) => {
  const { match } = useResponsive();
  const { t, formatPrice } = useLocalizationContext();
  const textStyle = useStyle(
    () => ({ color: palette.textColor }),
    [palette.textColor],
  );
  const borderStyle = useStyle(
    () => ({ borderRightColor: palette.dividerColor }),
    [palette.dividerColor],
  );

  const iconStyle = useStyle(() => ({
    flexShrink: 0,
    paddingLeft: match([
      theme.spacing.small,
      theme.spacing.small,
      theme.spacing.medium,
    ]),
  }));

  return (
    <VStack
      testID="sweetpass.home-hero-stats.detailed"
      divider
      dividerColor={palette.dividerColor}
      customSpacing={0}
    >
      {amountSaved ? (
        <View style={styles.heroStatsRow}>
          <TitleText style={textStyle} size={match([3, 2])}>
            {t('sweetpass.home.stats.detailed.saved', {
              amount: formatPrice(amountSaved, 'USD').split('.')[0], // TODO: is there a better way without interpolating $ manually?
            })}
          </TitleText>
        </View>
      ) : null}

      <HStack itemsPerRow={2} customSpacing={0}>
        <Pressable
          testID="sweetpass.home-hero-stats.challenges"
          accessibilityRole="button"
          style={[styles.heroStatsRow, styles.heroStatsLeft, borderStyle]}
          onPress={navigateToChallenges}
        >
          <TitleText style={textStyle}>{challengesCount}</TitleText>

          <View style={styles.heroStatsRow}>
            <TitleText style={textStyle} size={match([3, 2])}>
              {challengesCount === 1
                ? t('sweetpass.home.stats.challenge')
                : t('sweetpass.home.stats.challenges')}
            </TitleText>

            <Icon
              color={palette.textColor}
              style={iconStyle}
              name="IconArrowRight"
              height={24}
              width={24}
            />
          </View>
        </Pressable>

        <Pressable
          testID="sweetpass.home-hero-stats.rewards"
          accessibilityRole="button"
          style={[styles.heroStatsRow, styles.heroStatsRight]}
          onPress={navigateToRewards}
        >
          <TitleText style={textStyle}>{rewardsCount}</TitleText>

          <View style={styles.heroStatsRow}>
            <TitleText style={textStyle} size={match([3, 2])}>
              {rewardsCount === 1
                ? t('sweetpass.home.stats.reward')
                : t('sweetpass.home.stats.rewards')}
            </TitleText>

            <Icon
              color={palette.textColor}
              style={iconStyle}
              name="IconArrowRight"
              height={24}
              width={24}
            />
          </View>
        </Pressable>
      </HStack>
    </VStack>
  );
};

// ─── Hooks ──────────────────────────────────────────────────────────────────

/**
 * This title is being handled without responsiveness due to a design request.
 *
 * Desktop
 *   SweetSans, 56px font size, 0.6px letter spacing
 *   Grenette Pro, 56px font size, -2.7px letter spacing
 *
 * Tablet
 *   SweetSans, 40px font size, 0.8px letter spacing
 *   Grenette Pro, 40px font size, -2px letter spacing
 *
 * Mobile
 *   SweetSans, 32px font size, 1px letter spacing
 *   Grenette Pro, 32px font size, -1.6px letter spacing
 */
const useCustomTitleStyle = (textColor: string, highlight: boolean) => {
  const { match } = useResponsive();

  const highlightFontSize = match([32, 40, 40, 56]);
  const highlightLineHeight = match([36, 40, 40, 56]);
  const highlightLetterSpacing = match([-1.6, -2, -2, -2.7]);
  const highlightFontFamily = 'GrenettePro-Regular';

  const regularFontSize = match([32, 40, 40, 56]);
  const regularLineHeight = match([36, 40, 40, 56]);
  const regularLetterSpacing = match([1, 0.8, 0.8, 0.6]);
  const regularFontFamily = 'SweetSans-Regular';

  return useStyle(
    () => ({
      color: textColor,
      fontSize: highlight ? highlightFontSize : regularFontSize,
      lineHeight: highlight ? highlightLineHeight : regularLineHeight,
      letterSpacing: highlight ? highlightLetterSpacing : regularLetterSpacing,
      fontFamily: highlight ? highlightFontFamily : regularFontFamily,
    }),
    [
      textColor,
      highlight,
      highlightFontSize,
      highlightLineHeight,
      highlightLetterSpacing,
      highlightFontFamily,
      regularFontSize,
      regularLineHeight,
      regularLetterSpacing,
      regularFontFamily,
    ],
  );
};

// ─── Styles ─────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  outerContainer: {
    flex: 1,
  },
  outerContainerXS: {
    marginBottom: theme.spacing.large,
  },
  outerContainerSM: {
    borderRadius: theme.spacing.medium,
  },
  wrapperXS: {
    paddingVertical: theme.spacing.medium,
  },
  wrapperSM: {
    paddingTop: theme.spacing.small,
    paddingHorizontal: theme.spacing.medium,
  },
  wrapperMD: {
    paddingTop: theme.spacing.large,
    paddingHorizontal: theme.spacing.large,
    paddingBottom: theme.spacing.medium,
  },
  heroStatsCta: {
    marginVertical: theme.spacing.small,
  },
  heroStatsRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: theme.spacing.medium,
  },
  heroStatsAlignBottom: {
    alignItems: 'flex-end',
  },
  heroStatsColumn: {
    alignItems: 'flex-start',
    paddingVertical: theme.spacing.medium,
  },
  heroStatsTitle: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  heroStatsNotice: {
    paddingVertical: theme.spacing.small,
  },
  heroStatsLeft: {
    paddingRight: theme.spacing.medium,
    borderRightWidth: 1,
  },
  heroStatsRight: {
    paddingLeft: theme.spacing.medium,
  },
});

// ─── TYPES ─────────────────────────────────────────────────────────────────────

type HeroStatsVariantProps = {
  readonly palette: SweetpassHeroStatsPalette;
  // eslint-disable-next-line react/no-unused-prop-types
  readonly amountSaved?: number;
  readonly rewardsCount: number;
  readonly challengesCount: number;
  readonly navigateToRewards: () => void;
  readonly navigateToChallenges: () => void;
} & Pick<ViewProps, 'testID'>;

type SweetpassHomeHeroStatsProps = HeroStatsVariantProps & {
  readonly content: Partial<Omit<SweetpassHomeStatsContent, 'type'>>;
  readonly navigateToBenefits: () => void;
  readonly detailedHeroStats: boolean;
} & Pick<ViewProps, 'testID'>;
